import FizConfig from "../FizConfig.ts";
import * as i18n from "../i18n.ts";
import * as FwDatas from "../FwDatas.ts";
import * as Fiz from "../util/Fiz.ts";
import {OS} from "../lib/os.ts"

import {
  CalendarBean, DishBean, DishRangeBean, FizRightBean, IComment, IEvent, IExtendedFamily,
  IExtendedFamilyMember, IFamily, IFamilyMember, IIMThread, IMedia, IProfile, IReminder,
  ISettingsPerFamily, IWallMessage, MealSettingsBean, PremiumRightBean, RecipeBean,
  RecipeExternalBean, RecipeIngredientBean, RecipeInputBean, RecurrencyDescriptor,
  ServerBeanPrototype, TimetableBean, CalendarTimetableBean,
  type DeviceTypeEnum, type FamilyAdminRightEnum, type IDevice, type IPlace, type JsonFw,
  type MealSettingsColumnBean, type MoodEnum, type OpenGraphMedia,
  type PremiumRightFlagEnum, type RefObjectTypeEnum, type ReminderUnitEnum
} from "./fizapilib.auto.ts";
import * as rrule from "../lib/rrule/rrule.js";
import * as rrulewriter from "../lib/rrule/rrule-writer.js";
//import  "../lib/rrule/rrule-writer.test.js";
import * as DateUtils from "../lib/DateUtils.ts";
import * as TimezoneUtils from "../lib/TimezoneUtils.ts";
import * as handlebars_family from "../handlebars_family.ts";
import IHasRecurrencyHelper, {type DateRange, type IHasRecurrency, type IHasRecurrencyOccurrence} from "../lib/IHasRecurrencyHelper.ts";
import ISharableByMemberProto from "./objects/ISharableByMember.ts";
import  "./objects/TaskBeans.ts";
import  "./objects/TaskCategoryBean.ts";
import {recipeCategoriesSyncCache, recipeSyncCache} from "../mealplanner/mealplannerApi.ts";
import { protoExtend } from "../lib/ObjectUtil.ts";
import { addParams } from "../lib/url.ts";
import { PlaceCache } from "./objects/MapBeans.ts";
import { getIconFromMimeType } from "../lib/FileUtils.ts";
import type { SvgFile } from "../svg/Icons.ts";

var
  getProfile = FwDatas.getProfile;

/** onDecode receives the untouched server JSON, where the dates are still ISO strings */
function rawDate(json: JsonFw, field: string): string {
  return json[field] as unknown as string;
}

/** CalendarBean.prototype is copied onto CalendarTimetableBean.prototype, so both share these */
interface CalendarExtensions {
  //ISharableByMemberProto
  isShared(): boolean;
  sharedMembers(): any[];
  sharedCartridge(onclick: string): string;
  /** computed in onDecode */
  details?: string;
  subtitlemodal?: string;
  detailinfolder?: string;
  isFolderRadio?: boolean;
  useCalendarColor?: boolean;
  subtitlewithsub?: string;
  sharedBy(): string | undefined;
  colorUI(): string;
  openSettingsOnActivate(): boolean;
  clickaction(): string;
}

/** the fields IFamilyMember.onDecode computes, IExtendedFamilyMember reuses it through .call() */
interface FamilyMemberDecodable {
  right?: FamilyAdminRightEnum;
  joinDate?: Date;
  hasJoined?: boolean;
  isFamilyFounder?: boolean;
  isAdmin?: boolean;
}

/** a member as IFamily.onDecode sorts and flags it */
interface FamilyMemberSortable extends FamilyMemberDecodable {
  isloggedaccount?: boolean;
  firstName?: string;
  isEditable?: boolean;
  isAdminEditableDisabled?: boolean;
}

/** the fields IFamily.onDecode computes, IExtendedFamily reuses it through .call() */
interface FamilyDecodable {
  pictureUri?: string;
  picture?: string;
  members: FamilyMemberSortable[];
  user?: FamilyMemberSortable;
}

/** the fields IProfile.onDecode computes, IExtendedFamilyMember reuses it through .call() */
interface ProfileDecodable {
  firstName?: string;
  name?: string;
  birthDateBean?: typeof birthDateBean;
}

/** the recipe category bean is not generated, recipeCategoriesSyncCache is therefore untyped */
export interface RecipeCategory {
  metaId?: string;
  categorySystem?: string;
  sortingIndex?: number;
  name?: string;
}

function recipeCategories(): RecipeCategory[] {
  return recipeCategoriesSyncCache.datas as RecipeCategory[];
}

export interface RecipeCategoryAssignment {
  assigned: boolean;
  category: RecipeCategory;
}

/** the fields RecipeInputBean.onDecode computes, RecipeBean and RecipeExternalBean reuse it through .apply() */
interface RecipeInputDecodable {
  url?: string;
  cookTime?: number;
  prepTime?: number;
  recipeCategoryIdList?: string[];
  host?: string;
  totalTime?: number;
  cookMin?: number;
  cookHour?: number;
  prepMin?: number;
  prepHour?: number;
  totalMin?: number;
  totalHour?: number;
  categories?: () => RecipeCategoryAssignment[];
}

/** a bean carrying moods, IComment.likers() is reused as RecipeBean.likers() */
interface Likeable {
  moodMap?: Map<number, Array<MoodEnum>>;
}

export interface RefActionData {
  text: string;
  author: any;
  date: Date;
}

export interface EventAttendee {
  author: any;
  accountId: number;
}

export interface EventTimezonedInfo {
  timeZone: string;
  start: Date;
  end: Date;
  buildDisplayInCalendar: () => void;
  i18ndateformat: () => string;
}

/** an occurrence is prototype-linked to its event, so it carries every event field too */
export type EventOccurrence = IEvent & IHasRecurrencyOccurrence;

export interface TimetableDay {
  index: number;
  name: string;
  value: string;
}

export interface TimetableWeek {
  name: string;
  index: number;
  letter: string;
  start: Date;
  end: Date;
  days: TimetableDay[];
  isCurrent?: boolean;
}

// Augmentation of the generated beans with the properties and methods added dynamically below
declare module "./fizapilib.auto.ts" {
  interface ServerBeanPrototype {
    authorIsMe(): boolean;
    author(): any;
  }
  interface CalendarBean extends CalendarExtensions {
    /** only a CalendarTimetableBean carries one, but an event reads it through its calendar */
    timetable?: TimetableBean;
  }
  interface CalendarTimetableBean extends CalendarExtensions {}
  interface RecurrencyDescriptor {
    /** copied from the owning event, the human readable text depends on it */
    allDay?: boolean;
    worthDisplaying(): boolean;
    text(): string | undefined;
  }
  interface IReminder {
    text(forType?: string, allDay?: boolean): string;
    simple(): string;
  }
  namespace IReminder {
    /** static, also used as is by the reminder-select widget */
    let html: (unit?: ReminderUnitEnum, value?: number, type?: string, allDay?: boolean) => string;
  }
  interface IIMThread {
    isunRead(): boolean | undefined;
    markAsRead(): void;
  }
  interface IEvent extends IHasRecurrency {
    /** the raw server JSON, kept for the edition modal */
    json?: JsonFw;
    rights?: {canDelete?: boolean, canUpdate?: boolean};
    /** start/end are startDate/endDate with the all day events read without their timezone */
    start?: Date;
    end?: Date;
    isBirthday?: boolean;
    personAge?: number;
    personName?: string;
    title?: string;
    /** the color as stored, this.color is truncated to 6 digits for the UI */
    colorDB?: string;
    attendees?: EventAttendee[];
    colorAttendee?: string;
    colorUI?: string;
    place?: IPlace;
    picture?: string;
    commentable?: boolean;
    isTimetableEvent?: boolean;
    isMealplannerEvent?: boolean;
    isBudgetEvent?: boolean;
    draggable?: boolean;
    /** set by the EventStore once the calendars are loaded */
    calendar?: CalendarBean;
    /** computed by buildDisplayInCalendar() */
    displayStartTime?: string;
    displayStartDate?: string;
    displayEndTime?: string;
    displayEndDate?: string;
    listInterval(range: DateRange): EventOccurrence[];
    isDefaultCalendar(): boolean;
    cartridgeLeft(): string;
    hasItsOwnRecurrency(): boolean;
    timetableWeekName(): string | null;
    reminder0(): IReminder;
    reminder1(): IReminder;
    reminder2(): IReminder;
    i18ndateformat(): string;
    isAttendee(idAsLong: number): boolean;
    buildDisplayInCalendar(): void;
    timezonedinfo(): EventTimezonedInfo | undefined;
    hasTimezone(): boolean;
  }
  interface IWallMessage {
    id?: string;
    /** the activity type, used as a CSS class and as a is<Type> flag */
    type?: string;
    isEvent?: boolean;
    isSettings?: boolean;
    isBirthday?: boolean;
    isMoodable?: boolean;
    isCommentable?: boolean;
    timestamp?: Date;
    person?: any;
    contactId?: string;
    contactName?: string;
    contact?: any;
    contactNames?: string;
    /** the medias grouped by kind, see mapMedia() */
    gallery?: Gallery;
    /** the text without its giphy links, see decodeGiphy() */
    textraw?: string;
    giphy?: string[];
    /** comment pagination, see paginateComments() */
    totalComments?: number;
    hasHiddenComments?: boolean;
    hasMaxComments?: boolean;
    refActionData(): RefActionData | undefined;
    family(): any;
  }
  interface IComment {
    metaId?: string;
    textraw?: string;
    giphy?: string[];
    /** comment pagination, see paginateComments() */
    editable?: boolean;
    hidden?: boolean;
    likers(): any[];
  }
  interface PremiumRightBean {
    requestlocation(): true | 'takepremium';
    requestautotrack(): true | 'takepremium';
    modalcreatecircleonClickAction(): string | undefined;
    addplaceOnClickAction(): string | undefined;
    inviteOnClickAction(): string | false;
    googleconnect(): string | undefined;
    icsconnect(): string | undefined;
    aiinbox(): string | undefined;
    aiimportupload(): string | undefined;
    outlookconnect(): string | undefined;
    mpadditemtolist(): string | undefined;
    recipecatlist(): string | undefined;
    specialdayscreate(): true | 'takepremium';
    specialdaysedit(): 'eventedit' | 'takepremium';
    specialdayscopy(): true | 'takepremium';
  }
  interface IProfile extends ProfileDecodable {}
  interface IFamily {
    picture?: string;
    /** the logged member of that family */
    user?: IFamilyMember;
  }
  interface IFamilyMember extends FamilyMemberSortable {}
  interface IExtendedFamilyMember extends FamilyMemberDecodable, ProfileDecodable {
    languageCode?: string;
    askEmail?: boolean;
    login?: string;
    roleText?: string;
    isKid(): boolean;
    devicesForupdate(): Partial<IDevice>[];
  }
  interface IExtendedFamily {
    picture?: string;
    /** the logged member of that family */
    user?: IExtendedFamilyMember;
  }
  interface IMedia {
    icon?: SvgFile;
    /** set by Gallery.lines() */
    thumbUrl?: string;
    widthPercent?: number | string;
    isVideo(): boolean;
    isIframable(): boolean;
    isImage(): boolean;
    isAudio(): boolean;
    cannotDisplay(): boolean;
    ratio(): number;
    ratioX(): number;
    getLimitedRatio(): number;
    getFlexBasis(height: number): number;
    editable(): boolean;
    url(w?: number, h?: number, s?: string): string;
    iconsimple(css: string): string;
    size(): string;
    fitToScreen(): IMedia;
  }
  interface MealSettingsBean {
    visibleColumns?: MealSettingsColumnBean[];
    hasOptional?: boolean;
  }
  interface MealSettingsColumnBean {
    displayName?: string;
    /** a custom column left empty, it can be hidden from the settings */
    optional?: boolean;
  }
  interface RecipeInputBean extends RecipeInputDecodable {}
  interface RecipeBean extends RecipeInputDecodable {
    /** the raw server JSON, kept to clone the recipe */
    originalJson?: JsonFw;
    clone?: () => RecipeBean;
    isMoodable?: boolean;
    color?: string;
    canShare?: boolean;
    canMood?: boolean;
    hasDetails?: boolean;
    hasCategories?: () => number;
    setFavorite?: (add: boolean) => void;
    /** the first media as an img tag, see the getter below */
    readonly icon?: string;
    likers(): any[];
  }
  interface RecipeExternalBean extends RecipeInputDecodable {
    /** the open graph images exposed as medias, only pictureUrl and url() are filled */
    medias?: OpenGraphMedia[];
  }
  interface OpenGraphMedia {
    pictureUrl?: string;
  }
  interface DishBean {
    /** the icon of the pointed recipe, see the getter below */
    readonly icon?: string;
  }
  interface TimetableBean {
    isOneTime?: boolean;
    isByUrl?: boolean;
    /** startDate without its timezone stripped, the server needs it that way */
    startDateUtc?: Date;
    multiWeek?: boolean;
    weeks?: TimetableWeek[];
    sharingType?: 'all' | 'custom' | 'none';
    /** a date inside the week range matching today */
    currentDate?: Date;
    currentWeekIndex?: number;
    buildWeeks(): TimetableWeek[];
    getWeek(date: Date): TimetableWeek | null;
  }
  interface ISettingsPerFamily {
    defaultReminderAsString(): string;
  }
  interface RecipeIngredientBean {
    fullname(): string;
  }
}

Object.assign(ServerBeanPrototype.prototype,{
  authorIsMe(this: ServerBeanPrototype & {accountId?: number}): boolean {
    return FwDatas.getAccountId()===this.accountId;
  },
  author(this: ServerBeanPrototype & {accountId?: number}): any {
    return getProfile(this.accountId);
  }
});


Object.assign(CalendarBean.prototype,ISharableByMemberProto,{
  sharedBy(this: CalendarBean): string | undefined {
    if(this.sharedByAccountId){
      return i18n.getMustache("calendars.sharedBy",getProfile(this.sharedByAccountId));
    }
  },
  colorUI(this: CalendarBean): string {
    return this.color || FizConfig.defaultColor;
  },
  onDecode(this: CalendarBean): void {
    FwDatas.cache(this)
    if(this.type==="TASKS"){
      this.details = i18n.get("calendars.detailtask");
    }else if(this.type==='PUBLIC_HOLIDAYS_FOLDER' && this.countryCode){
      this.details = i18n.get("calendars.publicholidaysin", i18n.getCountry(this.countryCode,this.countryLang));
    }else{
      this.details = this.location;
    }
    if(this.type==="TIMETABLE"){
      this.subtitle = i18n.get("calendars.defaulttimetable",this.name);
      this.subtitlemodal = null;
    }else if(this.type==="MEALPLANNER"){
      this.subtitlemodal = null;
    }else{
      this.subtitlemodal = this.subtitle;
    }
    if(this.type==='PUBLIC_HOLIDAYS'){
      this.detailinfolder = i18n.get("calendars.publicholidays")
    }else if(this.type==='PUBLIC_HOLIDAYS_ONLY'){
      this.detailinfolder = i18n.get("calendars.publicholidaysonly")
    }else{
      this.detailinfolder = this.subtitle;
    }

    this.isFolderRadio = this.type==='PUBLIC_HOLIDAYS_FOLDER';

    this.useCalendarColor = !this.useEventColor;

    this.subtitlewithsub = this.subtitle;
    if(this.calendars){
      var activatedSub = this.calendars.filter(c=>c.activated);
      for(var i=0;i<activatedSub.length;i++){
        this.subtitlewithsub += (i==0)?" - ":", ";
        this.subtitlewithsub += activatedSub[i].subtitle;

      }
    }
  },
  openSettingsOnActivate(this: CalendarBean): boolean {
    if(this.activated) return false;
    if(this.calendars){
      for(let i=0;i<this.calendars.length;i++){
        if(this.calendars[i].activated) return false;
      }
      return true;
    }
    return false;
  },

  clickaction(this: CalendarBean): string {
    //if activated can deactivate
    if(this.activated){
      return "evtcalactivate|"+this.metaId+"|false";
    }
    //set all stuff to subcalendars
    if(this.calendars){
      for(let i=0;i<this.calendars.length;i++){
        if(this.calendars[i].activated){
          return "evtcalactivate|"+this.metaId+"|true";
        }
      }
      return "caledit|"+this.metaId;
    }
    //activate
    return "evtcalactivate|"+this.metaId+"|true";
  }
});
Object.assign(CalendarTimetableBean.prototype,CalendarBean.prototype);

Object.assign(RecurrencyDescriptor.prototype,{
  worthDisplaying(this: RecurrencyDescriptor): boolean {
    return this.recurrency!=='NONE';
  },
  text(this: RecurrencyDescriptor): string | undefined {
    if(this.recurrency!=='NONE'){
      var options = rrule.RRule.parseString(this.rrule);
      if(this.allDay){
        //l'heure d'été à aukland met la zone alors on s'assure qu'on sera bien le bon jour avec cette bidouille
        if(options.until){
          options.until = new Date(options.until.toISOString().substring(0,10)+'T23:59:59')
        }
      }
      try{
        return rrulewriter.write(options);
      }catch(e){
        console.log(e);
        return "";
      }
    }
  }

});

Object.assign(IReminder.prototype,{
  text(this: IReminder, forType?: string, allDay?: boolean): string {
    return IReminder.html(this.reminderUnit,this.reminderValue,forType,allDay);
  },
  simple(this: IReminder): string {
    return this.reminderType==='NONE'?"":this.reminderUnit+'-'+this.reminderValue;
  }
});
IReminder.html = function(unit?: ReminderUnitEnum, value?: number, type?: string, allDay?: boolean): string {
  if(!unit){
    return i18n.get('reminder.none');
  }
  if(value===0){
    if(type==="task"){
      return i18n.get('reminder.atduedate');
    }else if(allDay){
      //return i18n.get('reminder.ateventallday');
    }else{
      return i18n.get('reminder.atevent');
    }
  }
  if(allDay){
    let
      twentyHeightOfMonth = DateUtils.dateSet(new Date(),{day:28,hour:9,minute:0,second:0,milli:0}),
      timeOfNotif,
      delta: Record<string, number> = {},
      dayDiff;
    delta[unit.toLowerCase()] = -value;

    timeOfNotif = DateUtils.dateAdd(twentyHeightOfMonth,delta);
    dayDiff = twentyHeightOfMonth.getDate()-timeOfNotif.getDate()

    if(dayDiff===0){
      return i18n.get('reminder.ateventday',DateUtils.format(timeOfNotif,'LT'));
    }else if(dayDiff === 1){
      return i18n.get('reminder.atevent1daybefore',DateUtils.format(timeOfNotif,'LT'));
    }else if(dayDiff === 2){
      return i18n.get('reminder.atevent2daybefore',DateUtils.format(timeOfNotif,'LT'));
    }else if(dayDiff >= 3){
      return i18n.get('reminder.ateventNdaybefore',{n:dayDiff,time:DateUtils.format(timeOfNotif,'LT')});
    }else{
      //must not happen
      return "";
    }
  }
  let unitKey: string;
  if(value>1){
    unitKey = {
      HOUR:"timeunit.hours",
      DAY:"timeunit.days",
      MINUTE:"timeunit.minutes"
    }[unit];
  }else{
    unitKey = {
      HOUR:"timeunit.hour",
      DAY:"timeunit.day",
      MINUTE:"timeunit.minute"
    }[unit];
  }
  return i18n.getMustache('reminder.text',{unit:i18n.get(unitKey),value:value});

}


/**
 * IM
 */

Object.assign(IIMThread.prototype,{

  isunRead(this: IIMThread): boolean | undefined {
    for(var i=0,len=this.participants.length;i<len;i++){
      var participant = this.participants[i];

      if(participant.isLogguedAccount){
        return participant.lastReadMessageDate==null ||
          (participant.lastReadMessageDate < this.messageListModifDate);
      }
    }
  },
  //use this when marking as read and do not want to refresh
  markAsRead(this: IIMThread): void {
    this.isunRead=function(){return false;};
  }
});


function formatDisplayDate(mom: Date, useToday = true): string {

  if (useToday && DateUtils.isToday(mom) ){
    return i18n.get("event.today");
  }

  if (useToday && DateUtils.isTomorrow(mom) ){
    return i18n.get("event.tomorrow");
  }

  return DateUtils.format(mom,'dddd LL');

}

Object.assign(IEvent.prototype,{
  listInterval(this: IEvent, range: DateRange): EventOccurrence[] {
    return (IHasRecurrencyHelper.listInterval(this,range) as EventOccurrence[]).map(event=>{
      if (event.isBirthday){
        event.personAge = event.occurenceIndex;
        if(event.occurenceIndex===0){
          event.text = handlebars_family.i18n("event.displayAge0",event,true);
        }else{
          event.text = handlebars_family.i18n("event.displayAge",event,true);
        }
        event.title = event.text;
      }

      return event;
    });
  },
  isDefaultCalendar(this: IEvent): boolean {
    return this.calendarId.indexOf('calendar/')===0;
  },

  cartridgeLeft(this: IEvent): string {
    var
      top = null;
    if(DateUtils.isToday(this.start)){
      top = i18n.get('event.today');
    }else if(DateUtils.isTomorrow(this.start)){
      top = i18n.get('event.tomorrow');
    }
    if(top===null||this.allDay){
      var
        contentMonth = '<p style="font-size:16px;line-height:24px">'+DateUtils.format(this.start,'MMMM')+'</p>',
        contentDay = '<p style="font-size:40px;line-height:40px">'+DateUtils.format(this.start,'D')+'</p>';
      //replace replace only the first which is expected
      return contentMonth+contentDay;
    }else{
      return '<p style="background-color:#BBBBBB;color:#FFF;font-size:80%;line-height:200%;width:100%;text-align:center">'+top+'</p><p class="flexauto" style="margin-top:10px;">'+DateUtils.format(this.start,'LT')+'</p>';
    }

  },

  onDecode(this: IEvent, json: JsonFw): void {
    this.json = json;
    TimezoneUtils.preferredTimezone.add(this.timeZone)
    //for event comming from wall
    FwDatas.cache(this)

    this.rights = this.editable?{canDelete:true,canUpdate:true}:{};

    this.recurrencyDescriptor.allDay = this.allDay;
    if (this.allDay){
      this.start = this.startDate = new Date(rawDate(json,'startDate').substring(0,19));
      this.end = this.endDate = json.endDate?new Date(rawDate(json,'endDate').substring(0,19)):this.startDate;

      if(this.recurrencyDescriptor.exdate){
        this.recurrencyDescriptor.exdate = this.recurrencyDescriptor.exdate.map((exdate)=>{
          return new Date(exdate.toISOString().substring(0,19));
        });
      }

    }else{
      this.start = this.startDate;
      this.end = this.endDate;

    }
    this.isBirthday = (
        this.eventType === "BIRTHDAY_CUSTOM" ||
        this.eventType === "BIRTHDAY" ||
        this.eventType === "BIRTHDAY_ACCOUNT"
    );
    if (this.isBirthday){
      this.personAge = this.occurenceIndex;
      this.personName = this.refPersonName ||this.text;
      if(this.occurenceIndex===0){
        this.text = handlebars_family.i18n("event.displayAge0",this,true);
      }else{
        this.text = handlebars_family.i18n("event.displayAge",this,true);
      }
    }

    if(this.eventType === "BIRTHDAY_ACCOUNT"){
      this.toAll = false;
      this.attendeeIds = [this.accountId]
    }

    // Event Object for FullCalendar
    //this.id = this.eventMasterId; // same id for repeating events
    this.title = this.text;
    this.colorDB = this.color;
    if(this.color && this.color.length>7){
      this.color = this.color.substring(0,7);
    }
    var attendees = this.attendees = [];
    if(this.attendeeIds){
      this.attendeeIds.forEach(function(id){
        attendees.push({author:getProfile(id),accountId:id})
      })
    }
    if(!this.toAll && this.attendees.length==1){
      this.colorAttendee = this.attendees[0].author.color
    }
    this.colorUI = this.colorDB || this.colorAttendee || (this.calendar?this.calendar.color:null) || FizConfig.defaultColor;

    if(this.placeId){
      this.place = PlaceCache.get(this.placeId);
      if(!this.where && this.place){
        this.where = this.place.name;
      }
    }

    if(this.eventType==='EXTERNAL'){
      this.author = null;
    }
    this.picture = this.medias&&this.medias.length?this.medias[0].pictureUrl:null;
    this.commentable = this.calendarId.indexOf('calendar/')===0;
    this.isTimetableEvent = this.calendarId.indexOf('calendarTimetable/')===0;
    this.isMealplannerEvent = this.calendarId.indexOf('calendarMealPlanner/')===0;
    this.isBudgetEvent = this.calendarId.indexOf('calendarBudget/')===0;
    this.draggable = this.editable && (!this.isTimetableEvent)&& (!this.isMealplannerEvent)
  },
  hasItsOwnRecurrency(this: IEvent): boolean {
    if(this.isTimetableEvent){
      //if it is not oneTime timetable, the recurrency comes from the timetable
      if(this.calendar.timetable?.recurrencyDescriptor.recurrency!=='NONE'){
        return false;
      }
    }
    return this.recurrencyDescriptor.recurrency!=='NONE'
  },
  timetableWeekName(this: IEvent): string | null {
    if(!this.isTimetableEvent){
      return null;
    }
    if(this.calendar.timetable.isOneTime){
      return null;
    }
    var
      startWeek = this.calendar.timetable.getWeek(this.start),
      endWeek = this.calendar.timetable.getWeek(this.end);

    if(startWeek==endWeek){
      return startWeek.name;
    }
    return i18n.get('timetable.week')+' '+[startWeek,endWeek].map(week=>week.letter).join(' & ');
  },
  reminder0(this: IEvent): IReminder {
    return this.reminderList[0];
  },
  reminder1(this: IEvent): IReminder {
    return this.reminderList[1];
  },
  reminder2(this: IEvent): IReminder {
    return this.reminderList[2];
  },
  i18ndateformat(this: IEvent): string {
    this.buildDisplayInCalendar()
    if(this.displayStartDate === this.displayEndDate){
      if (this.allDay){
        return i18n.getMustache("event.singleDayAllDayFormat",this);
      } else if (this.displayStartTime === this.displayEndTime){
        return i18n.getMustache("event.pinpointDateFormat",this);
      } else {
        return i18n.getMustache("event.singleDayFormat",this);
      }
    } else{
      if (this.allDay){
        return i18n.getMustache("event.multipleDayAllDayFormat",this);
      } else {
        return i18n.getMustache("event.multipleDayFormat",this);
      }
    }
  },
  isAttendee(this: IEvent, idAsLong: number): boolean {
    return this.toAll || this.attendeeIds.indexOf(idAsLong)>-1 ;
  },

  buildDisplayInCalendar(this: IEvent): void {
    this.displayStartTime = DateUtils.format(this.start,'LT');
    this.displayStartDate = formatDisplayDate(this.start,true);

    this.displayEndTime = DateUtils.format(this.end,'LT');
    this.displayEndDate = formatDisplayDate(this.end,true);

    if(this.isTimetableEvent){
      this.displayStartDate =  DateUtils.format(this.start,'dddd');
      this.displayEndDate =  DateUtils.format(this.end,'dddd');

    }else{
      //if two distinct day we do not want today or tomorrow because it might cause weird stuff
      if(this.displayStartDate === this.displayEndDate){
        this.displayStartDate = formatDisplayDate(this.start,false);
        this.displayEndDate = formatDisplayDate(this.end,false);
      }
    }


  },
  timezonedinfo(this: IEvent): EventTimezonedInfo | undefined {
    if(this.hasTimezone()){
      return {
        timeZone:this.timeZone,
        start:TimezoneUtils.toZone(this.start,this.timeZone),
        end:TimezoneUtils.toZone(this.end,this.timeZone),
        buildDisplayInCalendar:this.buildDisplayInCalendar,
        i18ndateformat:this.i18ndateformat,

      }
    }
  },
  hasTimezone(this: IEvent): boolean {
    return !!this.timeZone && this.timeZone !== OS.getTimeZone()
  }
});


/** an activity carrying paginated comments */
interface Commentable {
  editable?: boolean;
  comments?: IComment[];
  totalComments?: number;
  hasHiddenComments?: boolean;
  hasMaxComments?: boolean;
}

function paginateComments(activity: Commentable): void {
  if(!activity.comments || ! activity.comments.length){
    return;
  }
  activity.comments.sort(function(a,b){
    return a.creationDate.getTime() - b.creationDate.getTime();
  });
  var
    accountId = FwDatas.getAccountId(),
    hideIdx = activity.comments.length-2;
  activity.comments.forEach(function(comment,idx){
    comment.editable = activity.editable || comment.accountId===accountId;
    comment.hidden = (idx<hideIdx);
  });
  activity.totalComments = activity.comments.length;
  activity.hasHiddenComments = ( hideIdx > 0 );
  activity.hasMaxComments = ( activity.totalComments >= 1000 );

}

Object.assign(IWallMessage.prototype,{
  refActionData(this: IWallMessage): RefActionData | undefined {
    if(this.type === 'event' || this.type === 'task'){
      return;
    }
    if(this.refAction==='COMMENTED'){
      return {text:i18n.get('refAction.commented'),author:getProfile(this.postAccountId),date:this.sortingDate};
    }else if(this.refAction==='MOOD_STAR'){
      return {text:i18n.get('refAction.liked'),author:getProfile(this.postAccountId),date:this.sortingDate};
    }
  },
  family(this: IWallMessage): any {
    return FwDatas.getFamily();
  },
  onDecode(this: IWallMessage, json: JsonFw): void {
    decodeGiphy(this);
    var
      contact,
      //the extra bag is free form JSON
      extra: any = this.extra,
      //is<Type> and isRefType<RefType> are computed from the ref type
      flags = this as unknown as Record<string, boolean>,
      // map of refType => CSS class for styling
      ACTIVITY_TYPES: Partial<Record<RefObjectTypeEnum, string>> = {
        MESSAGE_THREAD:'message',
        PLACE_IN:'location',
        PLACE_OUT:'location',
        EVENT:'event',
        EVENT_CREATED: 'event',
        EVENT_UPDATED: 'event',
        EVENT_COMMENTED: 'event',
        TASKLIST: 'list',
        TASK_CREATED: 'task',
        TASK_UPDATED: 'task',
        TASK_COMMENTED: 'task',
        TASK_ASSIGNED: 'task',
        TASK_MARKED_COMPLETED: 'task',
        STATUS:'status',
        SINGLE_PICTURE: 'pictures',
        MULTIPLE_PICTURE: 'pictures',
        SINGLE_PICTURE_COMMENTED: 'pictures',
        MULTIPLE_PICTURE_COMMENTED: 'pictures',
        ORANGE_VVM_VOICE_MSG: 'amy',
        ORANGE_VVM_MISSED_CALL: 'amy',
        ORANGE_THUNDERSTORM_ALERT: 'amy',
        FAMILY_CREATED: 'settings',
        INVITATION_SENT: 'settings',
        MEMBER_JOIN: 'settings',
        PROFILE_CHANGED: 'settings',
        ACCOUNT_DELETED: 'settings'
      };
    this.id = this.wallMessageId;
    paginateComments(this);
    // activity type is used as a CSS class for styling
    this.type = ACTIVITY_TYPES[this.refType]||"hide";
    flags["is"+this.type.charAt(0).toUpperCase()+this.type.slice(1)] = true;
    flags["isRefType"+this.refType] = true;

    if(!this.isEvent){
      mapMedia(this);
    }

    this.isMoodable = this.refType !== 'FAMILY_CREATED';
    this.isCommentable = this.refType !== 'FAMILY_CREATED';

    if(this.type==='location' && this.refId && !this.place){
      this.place = FwDatas.get(this.refId);
    }
    this.timestamp = this.creationDate;
    if(this.isEvent){
      if(this.event.isBirthday){
        this.isBirthday = true;
      }
      this.timestamp = this.event.startDate;
      this.event.comments = null;
    }
    if (this.isSettings){
      // another family member, different from the author
      this.person = getProfile(this.refId);
      if((this.person===undefined ||this.person===null ) && extra){
        this.person = extra.profile;
      }
      // a single contact
      if(extra){
        if((contact = extra.contact)){
          this.contactId = contact.id;
          this.contactName = contact.name;
          this.contact = FwDatas.get(this.contactId);
        }
        if(extra.contacts){
          this.contactNames = extra.contacts.map(contact=>contact.name).join(", ");
        }
      }
    }
    FwDatas.cacheWall(this);
    this.editable = json.editable?this.editable:FwDatas.getAccountId()===this.accountId;
  }
});



Object.assign(IComment.prototype,{
  likers(this: Likeable): any[] {
    var
      currentAccountId = FwDatas.getAccountId().toString(),
      //moodMap is decoded by decodeMap() which builds a plain object, not a Map
      moodByAccountId = this.moodMap as unknown as Record<string, Array<MoodEnum>>,
      res = Object.entries(moodByAccountId).map(([accountId,mood])=>{
        if(currentAccountId===accountId) return;
        if(mood.indexOf('STAR')!==-1){
          return getProfile(accountId);
        }
      }).filter(prof=>prof!==undefined);

    if(res.length>5){
      var
        iconText = "+"+(res.length-3),
        toAdd: {thumb: string, name?: string} = {thumb:Fiz.getDefaultUrl(iconText.length===2?iconText:iconText.substring(1),"#cccccc")},
        removed = res.splice(3,res.length-3,toAdd);
      toAdd.name = removed.map(v=>v.name).join(', ');
    }
    return res;

  },
  onDecode(this: IComment): void {
    this.metaId = this.commentId;
    if(this.mood==='STAR'){
      this.text = '❤';
    }
    decodeGiphy(this);
    FwDatas.cache(this);
  }
});

Object.assign(PremiumRightBean.prototype,{
  requestlocation(this: PremiumRightBean): true | 'takepremium' {
    return this.geoLocAvailable?true:"takepremium";
  },
  requestautotrack(this: PremiumRightBean): true | 'takepremium' {
    return this.geoLocAvailable?true:"takepremium";
  },
  modalcreatecircleonClickAction(this: PremiumRightBean): string | undefined {
    return ({
      OK:'modalcreatecircle',
      KO_LIMIT:'createcirclequotareached'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.canFoundFamily];
  },
  addplaceOnClickAction(this: PremiumRightBean): string | undefined {
    return ({
      OK:'addplace'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.canCreatePlace];
  },
  inviteOnClickAction(this: PremiumRightBean): string | false {
    return ({
      OK:'invitemenu',
      KO_LIMIT:'invitequotareached',
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.canInvite]||false;

  },
  googleconnect(this: PremiumRightBean): string | undefined {
    return ({
      OK:'googleconnect',
      KO_PREMIUM:'takepremium'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.canLinkGoogleCalendar];
  },
  icsconnect(this: PremiumRightBean): string | undefined {
    return ({
      OK:'icsconnect',
      KO_PREMIUM:'takepremium'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.canLinkCalendarByUrl];

  },
  aiinbox(this: PremiumRightBean): string | undefined {
    return ({
      OK:'aiinboxmodal',
      KO_PREMIUM:'takepremium'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.aiImport];
  },
  aiimportupload(this: PremiumRightBean): string | undefined {
    return ({
      OK:'aiimportupload',
      KO_PREMIUM:'takepremium'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.aiImportByUpload];
  },
  outlookconnect(this: PremiumRightBean): string | undefined {
    return ({
      OK:'outlookconnect',
      KO_PREMIUM:'takepremium'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.canLinkOutlookCalendar];
  },
  mpadditemtolist(this: PremiumRightBean): string | undefined {
    return ({
      OK:'mpadditemtolist',
      KO_PREMIUM:'takepremium'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.recipeExportToShoppingListFlag];
  },
  recipecatlist(this: PremiumRightBean): string | undefined {
    return ({
      OK:'recipecatlist',
      KO_PREMIUM:'takepremium'
    } as Partial<Record<PremiumRightFlagEnum, string>>)[this.recipeCustomCategoriesFlag];
  },
  //FIXME specialDays is a PremiumRightFlagEnum, so always truthy, even when it is 'KO_PREMIUM'
  specialdayscreate(this: PremiumRightBean): true | 'takepremium' {
    return this.specialDays?true:'takepremium';
  },
  specialdaysedit(this: PremiumRightBean): 'eventedit' | 'takepremium' {
    return this.specialDays?'eventedit':'takepremium';
  },
  specialdayscopy(this: PremiumRightBean): true | 'takepremium' {
    return this.specialDays?true:'takepremium';
  }


});
export function birthDateBean(this : {birthDate:Date,birthDateNoYear:boolean}){
  if(this.birthDate){
    const
      birthDateDisplay = this.birthDateNoYear?DateUtils.format(this.birthDate,"DM"):DateUtils.format(this.birthDate,"LL"),
      birthDateLocal = DateUtils.getNextBirthday(this.birthDate),
      deltaDays =  DateUtils.getDeltaDays(birthDateLocal);

    return {
      birthDateDisplay,
      deltaDays,
      labelDays:deltaDays>1?'timeunit.days':'timeunit.day'
    }
  }
}
/** shared with IExtendedFamilyMember, which is not a subclass of IProfile */
function decodeProfile(profile: ProfileDecodable): void {
  profile.name = profile.firstName;
  profile.birthDateBean = birthDateBean
}

Object.assign(IProfile.prototype,{
  onDecode(this: IProfile): void {
    decodeProfile(this);
  },
  toString : function(this: IProfile): string {
    return this.name;
  }
});

/** shared with IExtendedFamily, which is not a subclass of IFamily */
function decodeFamily(family: FamilyDecodable): void {
  family.picture = family.pictureUri;
  family.user = family.members.find(member=>member.isloggedaccount)
  family.members.sort(function(a,b){
    if(b.isloggedaccount){
      return 1;
    }
    if(a.isloggedaccount){
      return -1;
    }
    if(a.right!==b.right){
      var pointsMap: Partial<Record<FamilyAdminRightEnum, number>> = {Admin:2, SuperAdmin:3, SubAccount:1};
      return (pointsMap[b.right]||0) - (pointsMap[a.right]||0);
    }
    //firstname is on in IExtendedFamilyMember not in IFamilyMember
    //this method is called via .super in IExtendedFamily so this ||'' avoid a NPE
    return (a.firstName||'').localeCompare((b.firstName||''))
  })
  family.members.forEach(function(member){
    // is user allowed to edit member details
    member.isEditable = (family.user?.isAdmin) || member.isloggedaccount;
    // is user allowed to grant/revoke administration rights of member
    member.isAdminEditableDisabled = member.isFamilyFounder || !family.user?.isAdmin;
  });
}

Object.assign(IFamily.prototype,{
  onDecode(this: IFamily): void {
    decodeFamily(this);
  }
});

/** shared with IExtendedFamilyMember, which is not a subclass of IFamilyMember */
function decodeFamilyMember(member: FamilyMemberDecodable): void {
  member.hasJoined = (member.joinDate!==undefined);
  member.isFamilyFounder = member.isAdmin = false;
  switch (member.right){
    case "SuperAdmin":
      member.isFamilyFounder = true;
      member.isAdmin = true;
      break;
    case "Admin":
      member.isAdmin = true;
      break;
  }
}

Object.assign(IFamilyMember.prototype,{
  onDecode(this: IFamilyMember): void {
    decodeFamilyMember(this);
  }
});
Object.assign(IExtendedFamilyMember.prototype,{
  onDecode(this: IExtendedFamilyMember): void {
    decodeFamilyMember(this);
    decodeProfile(this);
    if(this.isloggedaccount){
      if (this.locale ){
        this.languageCode = this.locale.replace("_","-");
      }
      if(!this.identifiers.find(id=>id.type==='Email')){
        this.askEmail = true
      }
    }

    this.devices.sort(function(a,b){
      return a.deviceType.localeCompare(b.deviceType) || (a.deviceId||'').localeCompare(b.deviceId||'');
    });
    this.login = this.identifiers.find(identifier=>identifier.type==='Login'||identifier.type==='Email')?.value
    var roleText: string[] = []
    if(this.isFamilyFounder){
      roleText.push(i18n.get('accountType.SuperAdmin'))
    }else if(this.isAdmin){
      roleText.push(i18n.get('accountType.Admin'))
    }
    if(this.role==='Unknown'){
    }else if(this.role==='Custom'){
      roleText.push(this.customRole);
    }else{
      roleText.push(i18n.get('role.'+this.role))
    }
    this.roleText = roleText.join(' & ')
  },
  isKid(this: IExtendedFamilyMember): boolean {
    if(this.birthDate){
      if(DateUtils.computeAge(this.birthDate)<13) return true;
    }
    return this.identifiers.find(identifier=>identifier.type==='Email')==undefined
  },
  devicesForupdate(this: IExtendedFamilyMember): Partial<IDevice>[] {
    var hasPhone: boolean, hasEmail: boolean, result: Partial<IDevice>[] = this.devices;
    this.devices.forEach(function(device,idx){
      if(device.deviceType==='EMAIL'){
        hasEmail = true;
      }else{
        hasPhone=true;
      }
    });
    if(!hasPhone){
      result = ([{deviceType:'MOBILE' as DeviceTypeEnum}] as Partial<IDevice>[]).concat(result);
    }
    if(!hasEmail){
      result = result.concat([{deviceType:'EMAIL' as DeviceTypeEnum}]);
    }

    return result;
  },


  toString(this: IExtendedFamilyMember): string {
    return this.name;
  }
});
Object.assign(IExtendedFamily.prototype,{
  onDecode(this: IExtendedFamily): void {
    decodeFamily(this);
  }
});

export interface GalleryLine {
  items: IMedia[];
  ratioX: number;
  hidden?: boolean;
}

export class Gallery {
  pictures: IMedia[] = [];
  videos: IMedia[] = [];
  audios: IMedia[] = [];
  /** filled by mapMedia() with every media, whatever its kind */
  medias: IMedia[] = [];
  length = 0;
  notificationkey?: string;

  lines(): GalleryLine[] {
    var
      line: GalleryLine = {items:[],ratioX:0},
      result = [line],
      medias = this.medias.filter(media=>media!==null && media.mimeType.indexOf('audio') !== 0);

    for(var i=0;i<medias.length;i++){
      if(line.ratioX>2.65 && i<medias.length-1){
        line = {items:[],ratioX:0};
        result.push(line)
      }
      line.ratioX += medias[i].ratioX();
      line.items.push(medias[i]);
    }
    //calculated width for each item
    result.forEach(function(line,idx){
      if(idx>1){
        line.hidden = true;
      }
      for(var i=0;i<line.items.length;i++){
        var item = line.items[i];
        if(line.items.length>1){
  //        line.items[i].widthPercent = (Math.max(1,(8/3)/line.invertedRatio))*50 /(4 /3*line.items[i].ratio());
          item.widthPercent = 100*item.ratioX()/line.ratioX;
          item.thumbUrl=item.url(512,512,'r');
        }else{
          item.thumbUrl=item.url(1024,1024,'r');
          item.widthPercent ="";
        }
      }
    });
    return result;

  }
}

/** a bean whose text may embed giphy links */
interface Giphyable {
  text?: string;
  textraw?: string;
  giphy?: string[];
}

var giphymatcher =  /https?:\/\/[^\s()‘’“”«»]*/g;
function decodeGiphy(obj: Giphyable): void {
  if(!obj.text) return;
  var giphy: string[] = [];
  obj.textraw = obj.text.replace(giphymatcher,function(match){
    if(match.indexOf('giphy.com')>0 || match.indexOf('gph.is')>0){
      giphy.push(match);
      return '';
    }else{
      return match;
    }
  }).trim();
  obj.giphy = giphy;

}

/** a bean carrying medias, they get grouped by kind into a Gallery */
interface HasMedia {
  medias?: IMedia[];
  gallery?: Gallery;
}

function mapMedia(iHasMedia: HasMedia): void {
  if(iHasMedia.medias && iHasMedia.medias.length){
    var g = iHasMedia.gallery = new Gallery();
    g.medias=iHasMedia.medias;
    iHasMedia.medias.forEach(function(media) {
      if(media===null){
        //extra security
      }else if (media.mimeType.indexOf('video') === 0) {
        g.videos.push(media);
      }else if(media.mimeType.indexOf('audio') === 0) {
        g.audios.push(media);
      }else{
        g.pictures.push(media);
      }
    });
    g.length = g.pictures.length+g.videos.length+g.audios.length;
    if(g.videos.length===g.length){
      g.notificationkey = g.length>1?'notifications.NEW_VIDEOS':'notifications.NEW_VIDEO';
    }else if(g.pictures.length===g.length){
      g.notificationkey = g.length>1?'notifications.NEW_PICTURES':'notifications.NEW_PICTURE';
    }else if(g.audios.length===g.length){
      g.notificationkey = g.length>1?'notifications.NEW_AUDIOS':'notifications.NEW_AUDIO';
    }else{
      g.notificationkey = 'notifications.NEW_MEDIAS';
    }
  }
}


var idxColor = 0;
Object.assign(IMedia.prototype,{
  isVideo(this: IMedia): boolean {
    return this.mimeType.indexOf('video/')===0;
  },
  isIframable(this: IMedia): boolean {
    return this.mimeType.indexOf('application/pdf')===0 || this.mimeType.indexOf('text/plain')===0;
  },
  isImage(this: IMedia): boolean {
    return this.mimeType.indexOf('image/')===0;
  },
  isAudio(this: IMedia): boolean {
    return this.mimeType.indexOf('audio/')===0;
  },
  cannotDisplay(this: IMedia): boolean {
    return !this.isVideo()&&!this.isImage()&&!this.isIframable()&&!this.isAudio()
  },
  ratio(this: IMedia): number {
    return this.resolutionY/this.resolutionX;
  },
  ratioX(this: IMedia): number {
    return this.resolutionX/this.resolutionY;
  },
  getLimitedRatio(this: IMedia): number {
    var ratio = this.ratioX() || 1;
    if(ratio<0.7) ratio = 0.7;
    if(ratio>1.7) ratio = 1.7;
    return ratio;
  },
  getFlexBasis(this: IMedia, height: number): number {
    return Math.round(height*this.getLimitedRatio())
  },

  editable(this: IMedia): boolean {
    return (this.pointedByWhat==="wall"||this.pointedByWhat==="family") && (this.accountId===FwDatas.getAccountId() || FwDatas.getUser().isAdmin);
  },
  url(this: IMedia, w?: number, h?: number, s?: string): string {
    //we want true url
    if(!w){
      return this.pictureUrl;
    }
    if(typeof s!=='string'||s.length!==1){
      s='c';
    }
    //we want bigger than the file we can just ask the file
    //FWS-957 chek on resolutionX/Y because weirdly 12>null
    if(s==='r' && this.resolutionX && this.resolutionY && w>this.resolutionX && h>this.resolutionY){
      //if it is a video we do not want the video but the preview
      if(this.isVideo()){
        return this.pictureUrl+'w='+this.resolutionX+'&h='+this.resolutionY+'&s='+s;
      }else{
        return this.pictureUrl;
      }
    }
    return this.pictureUrl+'w='+w+'&h='+h+'&s='+s;
  },
  iconsimple(this: IMedia, css: string): string {
    return `<icon style="${css}">${this.icon}</icon>`
  },
  size(this: IMedia): string {
    var unit = 0, crawler = this.datasize;
    while(crawler>1000){
      unit++;
      crawler /= 1000;
    }
    if(crawler<10){
      crawler = Math.round(10*crawler)/10;
    }else{
      crawler = Math.round(crawler);
    }
    return crawler+i18n.get("sizeunit."+['B','KB','MB','GB','TB'][unit]);
  },
  fitToScreen(this: IMedia): IMedia {
    if(!this.resolutionX || !this.resolutionY) return this;
    var
      maxWidth = (Math.ceil(window.screen.availWidth/512)*512),
      maxHeight = (Math.ceil(window.screen.availHeight/512)*512);
    if(this.resolutionX>maxWidth || this.resolutionY>maxHeight){
      var
        reduceRatio = Math.min(maxWidth/this.resolutionX,maxHeight/this.resolutionY);
      return protoExtend(this,{
        pictureUrl:addParams(this.pictureUrl,{w:maxWidth,h:maxHeight,s:'r'}),
        resolutionX:this.resolutionX*reduceRatio,
        resolutionY:this.resolutionY*reduceRatio,
        ratio:this.resolutionX/this.resolutionY
      })
    }
    return this;
  },
  onDecode(this: IMedia): void {
    FwDatas.cacheIMedia(this)
    if(!this.mimeType){
      this.mimeType = "";
    }
    if(!this.color){
      this.color = ["#CAD6D7","#CACED1","#D5D6D4","#D6D4CC","#CFCBC4"][idxColor++ %5];
    }
    //icon
    this.icon = getIconFromMimeType(this.mimeType);

    if(this.filerModifDate==null){
      this.filerModifDate = this.creationDate;
    }
  }


});




MealSettingsBean.prototype.onDecode=function(this: MealSettingsBean): void {
  const columnLabel: Record<string, string> = i18n.get("mp.cols");
  this.visibleColumns = this.columns.filter(column=>{
    column.displayName = column.name||columnLabel[column.type];
    column.optional = (column.type==="CUSTOM1"||column.type==="CUSTOM2") && !column.name && !column.display;
    this.hasOptional = this.hasOptional || column.optional;
    if(column.emoji==='🍽') column.emoji='🍽️'; //rewrite with colored one emoji
    return column.display;
  })
  this.visibleColumns.sort(function(a,b){return a.timeSlot<b.timeSlot?-1:1})
}

RecipeInputBean.prototype.onDecode=function(this: RecipeInputDecodable): void {
  try{
    this.host = new URL(this.url).hostname;

  }catch(e){}

  this.totalTime = 0;
  if(this.cookTime){
    this.totalTime += this.cookTime;
    this.cookMin = this.cookTime%60;
    this.cookHour = (this.cookTime - this.cookMin) /60;
  }
  if(this.prepTime){
    this.totalTime += this.prepTime;
    this.prepMin = this.prepTime%60;
    this.prepHour = (this.prepTime - this.prepMin) /60;
  }
  if(this.totalTime){
    this.totalMin = this.totalTime%60;
    this.totalHour = (this.totalTime - this.totalMin) /60;
  }
  let thiz = this;
  this.categories = function(): RecipeCategoryAssignment[] {
    return recipeCategories().map(trueCat=>{
      return {
        assigned:thiz.recipeCategoryIdList && thiz.recipeCategoryIdList.indexOf(trueCat.metaId)>-1,
        category:trueCat
      }
    }).filter(catContainer=>catContainer.category.categorySystem!=="FAVORITE")
  }

}
const
  FIZRIGHTALL = new FizRightBean({"canDelete":"true","canUpdate":"true"} as any),
  FIZRIGHTNONE = new FizRightBean({"canDelete":"false","canUpdate":"false"} as any);

RecipeBean.prototype.onDecode=function(this: RecipeBean, json: JsonFw): void {
  //thumb and moodStarShortcut are shadowed with values of another type than the generated ones,
  //the templates read {{thumb}} and {{moodStarShortcut}} either way
  const shadowed = this as any;

  this.originalJson = json;
  if(!this.rights){
    //no rights sent means no right at all
    this.rights = {} as FizRightBean;
  }
  this.clone = function(){return new RecipeBean(json as any)}
  this.isMoodable = true;
  this.color = this.ingredients===undefined?"#eee":FizConfig.defaultColor;
  if(this.medias && this.medias.length){
    shadowed.thumb = this.medias[0].url(120,120,'c');
  }
  this.canShare = true;
  this.canMood = true;
  //repair default values
  if(this.isMealSuggestion===null){
    this.isMealSuggestion = true;
  }
  if(this.isRecipe===null){
    this.isRecipe = false;
  }
  this.hasDetails = !!this.ingredients || !!this.description || !!this.instructions;

  let thiz = this;
  this.hasCategories =function(){
    return thiz.categories().filter(cat=>cat.assigned).length;
  }
  shadowed.moodStarShortcut =function(){
    let catFavorite = recipeCategories().filter(category=>category.categorySystem==="FAVORITE")[0];
    return catFavorite && thiz.recipeCategoryIdList.indexOf(catFavorite.metaId)!==-1;
  }
  this.setFavorite =function(add){
    var
      favId = recipeCategories().filter(category=>category.categorySystem==="FAVORITE")[0].metaId,
      idx = thiz.recipeCategoryIdList.indexOf(favId);
    if(add && idx===-1){
      thiz.recipeCategoryIdList.push(favId);
    }
    if(!add && idx!==-1){
      thiz.recipeCategoryIdList.splice(idx,1);
    }
  }

  RecipeInputBean.prototype.onDecode.apply(this,[json])
}
RecipeBean.prototype.likers = IComment.prototype.likers;
RecipeExternalBean.prototype.onDecode=function(this: RecipeExternalBean, json: JsonFw): void {
  RecipeInputBean.prototype.onDecode.apply(this,[json])

  this.medias = this.images.map(openGraphMediaBean=>{
    openGraphMediaBean.pictureUrl = openGraphMediaBean.secureUrl;
    //the generated url is a string, it is shadowed with the IMedia url() method so the templates can share it
    (openGraphMediaBean as any).url = function(this: OpenGraphMedia){return this.pictureUrl};
    return openGraphMediaBean;
  })
}

DishRangeBean.prototype.onDecode=function(this: DishRangeBean): void {
  this.list.sort(function(a,b){return a.sortingIndex-b.sortingIndex})
}

DishBean.prototype.onDecode=function(this: DishBean): void {
  FwDatas.cache(this);
  //TODO remove this when the sevrer will send the datas
  if(this.rights===undefined){
    this.rights=FIZRIGHTALL;
  }
}
Object.defineProperty(RecipeBean.prototype, "icon", {
  get(this: RecipeBean) {
    if(this.medias.length>0){
      return "<img src='"+this.medias[0].url(120,120,'c')+"'/>"
    }
  }
});


Object.defineProperty(DishBean.prototype, "icon", {
  get(this: DishBean) {
    return recipeSyncCache.get(this.recipeId)?.icon;
  }
});

TimetableBean.prototype.onDecode = function(this: TimetableBean, json: JsonFw): void {
  this.isOneTime = this.recurrencyDescriptor.recurrency === 'NONE';
  this.isByUrl = this.icalSubscriptionUrl!==undefined;
  if(!this.isOneTime){
    this.startDate = new Date(rawDate(json,'startDate').substring(0,10)+'T00:00');
    this.startDateUtc = new Date(rawDate(json,'startDate'));
    this.endDate = new Date(rawDate(json,'endDate').substring(0,10)+'T00:00');
    this.finishDate = new Date(rawDate(json,'finishDate').substring(0,10)+'T00:00');
    this.multiWeek = this.recurrencyDescriptor.recurrencyInterval>1;
    this.weeks = this.buildWeeks();
  }
  this.sharingType = this.sharedToAll?'all':(this.sharedMemberIds.length>1?'custom':'none');
  //FIXME it should never happen
  if(this.rights===undefined){
    this.rights=FIZRIGHTNONE;
  }
  this.rights.canShare = this.accountId === FwDatas.getAccountId()
}
TimetableBean.prototype.buildWeeks = function(this: TimetableBean): TimetableWeek[] {
  var
    result: TimetableWeek[] = [],
    weekCount = this.recurrencyDescriptor.recurrencyInterval,
    days = DateUtils.getWeekDays(this.calendarFirstDayOfWeek%7),
    crawlerWeek = this.startDate,
    crawlerDay = new Date(this.startDate);

  for(var i=0;i<weekCount;i++){
    var
      weekEnd = new Date(crawlerWeek),
      letter = String.fromCharCode(65+((i+this.firstOccurrenceOffset)%weekCount));
    weekEnd.setDate(weekEnd.getDate()+7)
    result.push({
      name:i18n.get('timetable.week')+' '+letter,
      index:i,
      letter:letter,
      start:crawlerWeek,
      end:weekEnd,
      days:days.map(dayInfo=>{
        //server does not accept date before the utcDate but in this case it should so I fixed like this
        let day = {index:crawlerDay.getDay(),name:dayInfo.name,value:DateUtils.forInputDate( crawlerDay)};
        crawlerDay.setDate(crawlerDay.getDate()+1)
        return day;
      })
    })
    crawlerWeek = weekEnd;
  }

  var
    currentWeekSeeker = new Date(),
    deltaWeekCount = 0;
  //firt of all we position our crawler in the range
  while(currentWeekSeeker<result[0].start){
    currentWeekSeeker = DateUtils.dateAdd(currentWeekSeeker,{day:7*weekCount});
  }
  while(currentWeekSeeker>result[result.length-1].end){
    currentWeekSeeker = DateUtils.dateAdd(currentWeekSeeker,{day:-7*weekCount});
  }
  this.currentDate = currentWeekSeeker;

  //then we seek in which week it is
  while(currentWeekSeeker>result[0].end){
    deltaWeekCount++;
    currentWeekSeeker = DateUtils.dateAdd(currentWeekSeeker,{day:-7});
  }
  result[deltaWeekCount].isCurrent = true;

  //console.log(result)
  this.currentWeekIndex = deltaWeekCount;

  return result;
}
TimetableBean.prototype.getWeek = function(this: TimetableBean, date: Date): TimetableWeek | null {
  var
    weekCount = this.recurrencyDescriptor.recurrencyInterval,
    endOfLastWeek=this.weeks[this.weeks.length-1].end;

  while(date>endOfLastWeek){
    date = DateUtils.dateAdd(date,{day:-7*weekCount})
  }
  for(let i=0;i<this.weeks.length;i++){
    if(date<=this.weeks[i].end){
      return this.weeks[i];
    }
  }
  console.log("week not found");
  return null;
}

ISettingsPerFamily.prototype.defaultReminderAsString = function(this: ISettingsPerFamily): string {
  return this.defaultReminderType==='NONE'?'':(this.defaultReminderUnit + '-' + this.defaultReminderValue)
}
Object.assign(RecipeIngredientBean.prototype,{
  fullname:function(this: RecipeIngredientBean): string {
    if(this.complementData===undefined){
      return this.name;
    }
    return this.name+", "+this.complementData
  }
})

export {mapMedia,decodeGiphy}
